<template>
	<div class="content-wrapper">
		<div class="container flex-grow-1 container-p-y">

			<div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-6 row-gap-4">
				<div class="d-flex flex-column justify-content-center">
					<div class="d-flex align-items-center gap-2 mb-1">
						<button class="btn btn-sm btn-outline-secondary" @click="$router.push('/background-check/list')">
							<i class="ri-arrow-left-line"></i>
						</button>
						<h4 class="mb-0" v-if="check">{{ check.firstName }} {{ check.lastName }}</h4>
					</div>
				</div>
				<div class="d-flex align-content-center align-items-center flex-wrap gap-2" v-if="check">
					<button v-if="check.status === 'approved' || check.status === 'rejected'"
						class="btn btn-sm btn-outline-primary" @click="exportPdf" :disabled="exporting">
						<span v-if="exporting" class="spinner-border spinner-border-sm me-1"></span>
						<i v-else class="ri-download-line me-1"></i>
						{{ $t('backgroundCheck.detail.exportPdf') }}
					</button>
					<span class="badge rounded-pill fs-6" :class="statusBadgeClass(check.status)">
						{{ statusLabel(check.status) }}
					</span>
				</div>
			</div>

			<div v-if="check" class="row g-4">
				<!-- Personal Info -->
				<div class="col-lg-6">
					<div class="card h-100">
						<h5 class="card-header">{{ $t('backgroundCheck.detail.personalInfo') }}</h5>
						<div class="card-body">
							<div class="row g-3">
								<div class="col-6">
									<small class="text-muted d-block">{{ $t('common.fields.firstName') }}</small>
									<strong>{{ check.firstName || '—' }}</strong>
								</div>
								<div class="col-6">
									<small class="text-muted d-block">{{ $t('common.fields.lastName') }}</small>
									<strong>{{ check.lastName || '—' }}</strong>
								</div>
								<div class="col-6">
									<small class="text-muted d-block">{{ $t('common.fields.email') }}</small>
									<strong>{{ check.email || '—' }}</strong>
								</div>
								<div class="col-6">
									<small class="text-muted d-block">{{ $t('common.fields.phone') }}</small>
									<strong>{{ check.phoneNumber || '—' }}</strong>
								</div>
								<div class="col-6">
									<small class="text-muted d-block">{{ $t('common.fields.birthDate') }}</small>
									<strong>{{ check.birthDate ? formatDate(check.birthDate) : '—' }}</strong>
								</div>
								<div class="col-6">
									<small class="text-muted d-block">{{ $t('backgroundCheck.detail.submittedOn') }}</small>
									<strong>{{ check.submittedAt ? formatDateTime(check.submittedAt) : '—' }}</strong>
								</div>
							</div>
							<div class="mt-4" v-if="confidenceScore !== null && check.verification && check.verification.status === 'approved'">
								<div class="d-flex align-items-center justify-content-between mb-2">
									<small class="text-muted">{{ $t('backgroundCheck.detail.confidenceScore') }}</small>
									<strong :class="confidenceScore > 90 ? 'text-success' : confidenceScore > 60 ? 'text-warning' : 'text-danger'">{{ confidenceScore }}%</strong>
								</div>
								<div class="progress" style="height: 6px;">
									<div class="progress-bar" role="progressbar"
										:style="{ width: confidenceScore + '%' }"
										:class="confidenceScore > 90 ? 'bg-success' : confidenceScore > 60 ? 'bg-warning' : 'bg-danger'">
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Verification Veriff -->
				<div class="col-lg-6">
					<div class="card h-100">
						<h5 class="card-header d-flex align-items-center justify-content-between">
							<span>{{ $t('backgroundCheck.detail.identityVerification') }}</span>
							<div class="d-flex align-items-center gap-2" v-if="check.verification && check.verification.status">
								<span class="badge rounded-pill" :class="veriffBadgeClass(check.verification)">
									{{ veriffLabel(check.verification) }}
								</span>
							</div>
						</h5>
						<div class="card-body" v-if="!canResendVerification">
							<div class="row g-3">
								<div class="col-6" v-if="check.verification.verifiedFirstName">
									<small class="text-muted d-block">{{ $t('backgroundCheck.detail.verifiedFirstName') }}</small>
									<strong>{{ check.verification.verifiedFirstName }}</strong>
								</div>
								<div class="col-6" v-if="check.verification.verifiedLastName">
									<small class="text-muted d-block">{{ $t('backgroundCheck.detail.verifiedLastName') }}</small>
									<strong>{{ check.verification.verifiedLastName }}</strong>
								</div>
								<div class="col-6" v-if="check.verification.verifiedDateOfBirth">
									<small class="text-muted d-block">{{ $t('common.fields.birthDate') }}</small>
									<strong>{{ formatDate(check.verification.verifiedDateOfBirth) }}</strong>
								</div>
							</div>
							<!-- Document info -->
							<div class="row g-3 mt-1" v-if="check.verification.document && check.verification.document.type">
								<div class="col-12"><hr class="my-1"></div>
								<div class="col-6">
									<small class="text-muted d-block">{{ $t('backgroundCheck.detail.idDocument') }}</small>
									<strong>{{ documentTypeLabel(check.verification.document.type) }}</strong>
								</div>
								<div class="col-6" v-if="check.verification.document.number">
									<small class="text-muted d-block">{{ $t('backgroundCheck.detail.number') }}</small>
									<strong>{{ check.verification.document.number }}</strong>
								</div>
								<div class="col-6" v-if="check.verification.document.country">
									<small class="text-muted d-block">{{ $t('backgroundCheck.detail.country') }}</small>
									<strong>{{ check.verification.document.country }}</strong>
								</div>
								<div class="col-6" v-if="check.verification.document.validUntil">
									<small class="text-muted d-block">{{ $t('backgroundCheck.detail.expiresOn') }}</small>
									<strong>{{ formatDate(check.verification.document.validUntil) }}</strong>
								</div>
							</div>
						</div>
						<div class="card-body text-center py-4" v-else-if="verificationNeedsRenewal">
							<i class="ri-error-warning-line text-danger mb-2 d-block" style="font-size: 24px;"></i>
							<p class="text-muted mb-2" style="font-size: 13px;">{{ $t('backgroundCheck.detail.verificationExpired') }}</p>
							<button class="btn btn-sm btn-primary" @click="resendVerificationLink" :disabled="resendLoading">
								<span v-if="resendLoading" class="spinner-border spinner-border-sm me-1"></span>
								<i v-else class="ri-refresh-line me-1"></i>
								{{ $t('backgroundCheck.detail.renewVerificationLink') }}
							</button>
							<p class="text-muted mb-0 mt-2" style="font-size: 12px;">{{ $t('backgroundCheck.detail.resendVerificationLinkHint') }}</p>
							<p class="text-muted mb-0 mt-1" style="font-size: 12px;" v-if="check.verification.lastReminderSentAt">
								{{ $t('backgroundCheck.detail.lastReminderSentAt', { date: formatDateTime(check.verification.lastReminderSentAt) }) }}
							</p>
						</div>
						<div class="card-body text-center py-4" v-else>
							<div class="spinner-border spinner-border-sm text-primary mb-2" role="status"></div>
							<p class="text-muted mb-2" style="font-size: 13px;">{{ $t('backgroundCheck.detail.waitingForVerification') }}</p>
							<button class="btn btn-sm btn-outline-primary" @click="resendVerificationLink" :disabled="resendLoading">
								<span v-if="resendLoading" class="spinner-border spinner-border-sm me-1"></span>
								<i v-else-if="resendSent" class="ri-check-line me-1"></i>
								<i v-else class="ri-mail-send-line me-1"></i>
								{{ resendSent ? $t('backgroundCheck.detail.verificationLinkSent') : $t('backgroundCheck.detail.resendVerificationLink') }}
							</button>
							<p class="text-muted mb-0 mt-2" style="font-size: 12px;">{{ $t('backgroundCheck.detail.resendVerificationLinkHint') }}</p>
							<p class="text-muted mb-0 mt-1" style="font-size: 12px;" v-if="check.verification && check.verification.lastReminderSentAt">
								{{ $t('backgroundCheck.detail.lastReminderSentAt', { date: formatDateTime(check.verification.lastReminderSentAt) }) }}
							</p>
						</div>
					</div>
				</div>

				<!-- Documents -->
				<div class="col-lg-12">
					<div class="card">
						<h5 class="card-header">{{ $t('backgroundCheck.detail.documents') }}</h5>
						<div class="card-body">
							<div class="row g-3">
								<div class="col-md-4">
									<div class="card border h-100">
										<div class="card-body text-center">
											<i class="ri-file-text-line" style="font-size: 32px;" :class="check.cvUrl ? 'text-primary' : 'text-muted'"></i>
											<p class="mt-2 mb-2"><strong>{{ $t('backgroundCheck.detail.cv') }}</strong></p>
											<a v-if="check.cvUrl" :href="check.cvUrl" target="_blank" class="btn btn-sm btn-outline-primary">
												<i class="ri-download-line me-1"></i> {{ $t('common.actions.download') }}
											</a>
											<span v-else class="text-muted">{{ $t('backgroundCheck.detail.notUploaded') }}</span>
										</div>
									</div>
								</div>
								<div class="col-md-4">
									<div class="card border h-100">
										<div class="card-body text-center">
											<i class="ri-file-text-line" style="font-size: 32px;" :class="check.coverLetterUrl ? 'text-primary' : 'text-muted'"></i>
											<p class="mt-2 mb-2"><strong>{{ $t('backgroundCheck.detail.coverLetter') }}</strong></p>
											<a v-if="check.coverLetterUrl" :href="check.coverLetterUrl" target="_blank" class="btn btn-sm btn-outline-primary">
												<i class="ri-download-line me-1"></i> {{ $t('common.actions.download') }}
											</a>
											<span v-else class="text-muted">{{ $t('backgroundCheck.detail.notUploaded') }}</span>
										</div>
									</div>
								</div>
								<div class="col-md-4">
									<div class="card border h-100" :class="b3BorderClass">
										<div class="card-body text-center position-relative">
											<button v-if="check.hasB3Extract" class="btn btn-sm btn-outline-primary position-absolute" style="top: 8px; left: 8px;" :title="b3Loading ? $t('backgroundCheck.detail.analysisInProgress') : $t('backgroundCheck.detail.retryB3Analysis')" @click="retryB3Analysis" :disabled="b3Loading">
												<span v-if="b3Loading" class="spinner-border spinner-border-sm"></span>
												<i v-else class="ri-refresh-line"></i>
											</button>
											<span v-if="check.b3Analysis && check.b3Analysis.status" class="position-absolute" style="top: 8px; right: 8px;">
												<i v-if="check.b3Analysis.status === 'clean'" class="ri-checkbox-circle-fill text-success" style="font-size: 20px;" :title="$t('backgroundCheck.detail.b3Clean')"></i>
												<i v-else-if="check.b3Analysis.status === 'mismatch'" class="ri-error-warning-fill text-warning" style="font-size: 20px;" :title="$t('backgroundCheck.detail.b3Mismatch') + ': ' + (check.b3Analysis.mismatches || []).join(', ')"></i>
												<i v-else-if="check.b3Analysis.status === 'not_clean'" class="ri-close-circle-fill text-danger" style="font-size: 20px;" :title="$t('backgroundCheck.detail.b3NotClean')"></i>
												<i v-else class="ri-question-line text-muted" style="font-size: 20px;" :title="$t('backgroundCheck.detail.b3Unreadable')"></i>
											</span>
											<i class="ri-file-text-line" style="font-size: 32px;" :class="check.hasB3Extract ? 'text-primary' : 'text-muted'"></i>
											<p class="mt-2 mb-2"><strong>{{ $t('backgroundCheck.detail.b3Extract') }}</strong></p>
											<span v-if="check.hasB3Extract" class="badge bg-label-secondary">
												{{ $t('backgroundCheck.detail.b3Provided') }}
											</span>
											<span v-else-if="check.b3Analysis && check.b3Analysis.status" class="badge bg-label-secondary">
												{{ $t('backgroundCheck.detail.b3AnalyzedNotStored') }}
											</span>
											<span v-else class="text-muted">{{ $t('backgroundCheck.detail.notUploaded') }}</span>
											<div v-if="check.b3Analysis && check.b3Analysis.mismatches && check.b3Analysis.mismatches.length > 0" class="mt-2">
												<small class="text-warning" v-for="(m, i) in check.b3Analysis.mismatches" :key="i">{{ m }}<br></small>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Gap Analysis -->
				<div class="col-lg-12">
					<div class="card">
						<h5 class="card-header d-flex align-items-center justify-content-between">
							<div>
								{{ $t('backgroundCheck.detail.cvAnalysis') }}
								<span class="badge bg-label-warning rounded-pill ms-2" v-if="check.gapAnalysis && check.gapAnalysis.gaps && check.gapAnalysis.gaps.length > 0">
									{{ check.gapAnalysis.gaps.length }}
								</span>
							</div>
							<button class="btn btn-sm btn-outline-primary" @click="retryGapAnalysis" :disabled="gapLoading">
								<span v-if="gapLoading" class="spinner-border spinner-border-sm me-1"></span>
								<i v-else class="ri-refresh-line me-1"></i>
								{{ gapLoading ? $t('backgroundCheck.detail.analysisInProgress') : $t('backgroundCheck.detail.retryAnalysis') }}
							</button>
						</h5>
						<div class="card-body">
							<div v-if="!check.gapAnalysis || (!check.gapAnalysis.status && !gapLoading)">
								<p class="text-muted mb-0">{{ $t('backgroundCheck.detail.analysisNotStarted') }}</p>
							</div>
							<div v-else-if="check.gapAnalysis && check.gapAnalysis.status === 'not_a_cv'">
								<p class="text-warning mb-0"><i class="ri-alert-line me-1"></i> {{ $t('backgroundCheck.detail.notACv') }}</p>
							</div>
							<div v-else-if="check.gapAnalysis && check.gapAnalysis.status === 'error'">
								<p class="text-danger mb-0"><i class="ri-error-warning-line me-1"></i> {{ $t('backgroundCheck.detail.analysisFailed') }}</p>
							</div>
							<div v-else-if="check.gapAnalysis && check.gapAnalysis.status === 'completed' && check.gapAnalysis.gaps && check.gapAnalysis.gaps.length > 0">
								<div class="table-responsive">
									<table class="table table-borderless">
										<thead>
											<tr class="border-bottom">
												<th class="bg-transparent fs-xsmall">{{ $t('backgroundCheck.detail.start') }}</th>
												<th class="bg-transparent fs-xsmall">{{ $t('backgroundCheck.detail.end') }}</th>
												<th class="bg-transparent fs-xsmall">{{ $t('backgroundCheck.detail.duration') }}</th>
												<th class="bg-transparent fs-xsmall">{{ $t('backgroundCheck.detail.description') }}</th>
											</tr>
										</thead>
										<tbody>
											<tr v-for="(gap, i) in check.gapAnalysis.gaps" :key="i">
												<td>{{ formatDate(gap.from) }}</td>
												<td>{{ formatDate(gap.to) }}</td>
												<td><span class="badge bg-label-warning">{{ gap.durationDays }} {{ $t('backgroundCheck.detail.days') }}</span></td>
												<td>{{ gap.description }}</td>
											</tr>
										</tbody>
									</table>
								</div>
							</div>
							<div v-else-if="check.gapAnalysis && check.gapAnalysis.status === 'completed'">
								<p class="text-muted mb-0"><i class="ri-checkbox-circle-line text-success me-1"></i> {{ $t('backgroundCheck.detail.noGapsDetected') }}</p>
							</div>

							<div v-else-if="check.gapAnalysis && check.gapAnalysis.status === 'pending'">
								<p class="text-muted mb-0"><i class="ri-loader-4-line me-1"></i> {{ $t('backgroundCheck.detail.analysisInProgress') }}</p>
							</div>

						</div>
					</div>
				</div>

				<!-- Non-aero experiences -->
				<div class="col-lg-12" v-if="check.gapAnalysis && check.gapAnalysis.nonAeroExperiences && check.gapAnalysis.nonAeroExperiences.length > 0">
					<div class="card">
						<h5 class="card-header d-flex align-items-center justify-content-between">
							<div>
								<i class="ri-alert-line text-warning me-1"></i>
								{{ $t('backgroundCheck.detail.nonAeroExperiences') }}
								<span class="badge bg-label-warning rounded-pill ms-2">{{ check.gapAnalysis.nonAeroExperiences.length }}</span>
							</div>
						</h5>
						<div class="card-body">
							<div class="table-responsive">
								<table class="table table-borderless">
									<thead>
										<tr class="border-bottom">
											<th class="bg-transparent fs-xsmall">{{ $t('backgroundCheck.detail.position') }}</th>
											<th class="bg-transparent fs-xsmall">{{ $t('backgroundCheck.detail.company') }}</th>
											<th class="bg-transparent fs-xsmall">{{ $t('backgroundCheck.detail.sector') }}</th>
											<th class="bg-transparent fs-xsmall">{{ $t('backgroundCheck.detail.period') }}</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="(exp, i) in check.gapAnalysis.nonAeroExperiences" :key="'nae-'+i">
											<td>{{ exp.role }}</td>
											<td>{{ exp.company }}</td>
											<td><span class="badge bg-label-warning">{{ exp.sector }}</span></td>
											<td>{{ exp.from }} — {{ exp.to }}</td>
										</tr>
									</tbody>
								</table>
							</div>
							<div v-if="check.gapAnalysis.hasAeroExperience === false" class="alert alert-danger mt-3 mb-0 py-2">
								<i class="ri-error-warning-line me-1"></i> <strong>{{ $t('backgroundCheck.detail.noAeroExperience') }}</strong>
							</div>
						</div>
					</div>
				</div>

				<!-- AI Analysis (only if Veriff completed) -->
				<div class="col-lg-12" v-if="check.verification && check.verification.status === 'approved'">
					<div class="card">
						<h5 class="card-header d-flex align-items-center justify-content-between">
							<span>{{ $t('backgroundCheck.detail.conclusion') }}</span>
							<button class="btn btn-sm btn-outline-secondary" @click="generateNotes" :disabled="notesLoading">
								<span v-if="notesLoading" class="spinner-border spinner-border-sm me-1"></span>
								<i v-else class="ri-sparkling-line me-1"></i>
								{{ aiAnalysis ? $t('backgroundCheck.detail.retry') : $t('backgroundCheck.detail.generate') }}
							</button>
						</h5>
						<div class="card-body">
							<div v-if="aiAnalysis" class="ai-analysis" style="font-size: 14px; line-height: 1.7;" v-html="renderMarkdown(aiAnalysis)"></div>
							<p v-else class="text-muted mb-0">{{ $t('backgroundCheck.detail.noAnalysis') }}</p>
							<div class="alert alert-success mt-3 py-2" v-if="autoApproved">
								<i class="ri-checkbox-circle-line me-1"></i> {{ $t('backgroundCheck.detail.autoApproved') }}
							</div>
						</div>
					</div>
				</div>

				<!-- Waiting for identity verification -->
				<div class="col-lg-12" v-else-if="!check.verification || check.verification.status !== 'approved'">
					<div class="card">
						<div class="card-body text-center py-5">
							<div class="spinner-border text-primary mb-3" role="status"></div>
							<h6 class="text-muted">{{ $t('backgroundCheck.detail.waitingForIdentity') }}</h6>
							<p class="text-muted mb-0" style="font-size: 13px;">{{ $t('backgroundCheck.detail.aiAvailableAfterVeriff') }}</p>
						</div>
					</div>
				</div>

				<!-- Notes + Actions -->
				<div class="col-lg-12" v-if="check.verification && check.verification.status === 'approved'">
					<div class="card">
						<h5 class="card-header">{{ $t('backgroundCheck.detail.teamNotes') }}</h5>
						<div class="card-body">
							<textarea class="form-control" id="manualNotes" :placeholder="$t('backgroundCheck.detail.notesPlaceholder')" v-model="manualNotes" rows="4" style="min-height: 100px; font-size: 14px;"></textarea>

							<div class="d-flex gap-2 mt-4">
								<button class="btn btn-success" @click="updateStatus('approved')" :disabled="statusLoading">
									<i class="ri-checkbox-circle-line me-1"></i> {{ $t('backgroundCheck.detail.approve') }}
								</button>
								<button class="btn btn-danger" @click="updateStatus('rejected')" :disabled="statusLoading">
									<i class="ri-close-circle-line me-1"></i> {{ $t('backgroundCheck.detail.reject') }}
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Loading -->
			<div class="text-center py-6" v-if="!check && !loadError">
				<div class="spinner-border text-primary" role="status"></div>
			</div>

			<!-- Error -->
			<div class="alert alert-danger" v-if="loadError">{{ loadError }}</div>
		</div>
		<div class="content-backdrop fade"></div>

		<!-- Selfie Modal -->
		<div class="modal fade" :class="{ show: selfieModalVisible }" :style="selfieModalVisible ? 'display: block;' : 'display: none;'" tabindex="-1" @click.self="selfieModalVisible = false">
			<div class="modal-dialog modal-dialog-centered">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">{{ $t('backgroundCheck.detail.verificationSelfie') }}</h5>
						<button type="button" class="btn-close" @click="selfieModalVisible = false"></button>
					</div>
					<div class="modal-body text-center">
						<img v-if="selfieUrl" :src="selfieUrl" alt="Selfie" style="max-width: 100%; max-height: 400px; border-radius: 8px;">
						<p v-else class="text-muted">{{ $t('common.loading') }}</p>
					</div>
				</div>
			</div>
		</div>
		<div class="modal-backdrop fade show" v-if="selfieModalVisible" @click="selfieModalVisible = false"></div>
	</div>
</template>

<script>
import HTTP from "@/axios";
import moment from "moment";
import "moment/locale/fr";
import { marked } from "marked";

moment.locale(localStorage.getItem('locale') || 'fr');

export default {
	name: "BackgroundCheckDetail",

	data() {
		return {
			check: null,
			aiAnalysis: "",
			manualNotes: "",
			statusLoading: false,
			gapLoading: false,
			b3Loading: false,
			notesLoading: false,
			resendLoading: false,
			resendSent: false,
			exporting: false,
			confidenceScore: null,
			autoApproved: false,
			selfieUrl: null,
			selfieLoading: false,
			selfieModalVisible: false,
			loadError: null,
		};
	},

	computed: {
		verificationStatus() {
			return this.check?.verification?.status || null;
		},

		// Session Veriff close sans résultat : le candidat doit recevoir un nouveau lien
		verificationNeedsRenewal() {
			return ["expired", "abandoned", "resubmission_requested"].includes(this.verificationStatus);
		},

		canResendVerification() {
			return !this.verificationStatus
				|| ["init", "started"].includes(this.verificationStatus)
				|| this.verificationNeedsRenewal;
		},
	},

	methods: {
		async fetchDetail() {
			try {
				const id = this.$route.params.id;
				const response = await HTTP(`/background-check/${id}`);
				this.check = response.data;
				this.aiAnalysis = this.check.aiAnalysis || "";
				this.manualNotes = this.check.manualNotes || "";
				this.confidenceScore = this.check.confidenceScore ?? null;
			} catch (error) {
				this.loadError = error?.response?.data?.message || this.$t('backgroundCheck.detail.loadError');
			}
		},

		async exportPdf() {
			this.exporting = true;
			try {
				const id = this.$route.params.id;
				const response = await HTTP.get(`/background-check/${id}/recap-pdf`);

				if (response.data?.base64) {
					const byteCharacters = atob(response.data.base64);
					const byteNumbers = new Array(byteCharacters.length);
					for (let i = 0; i < byteCharacters.length; i++) {
						byteNumbers[i] = byteCharacters.charCodeAt(i);
					}
					const byteArray = new Uint8Array(byteNumbers);
					const blob = new Blob([byteArray], { type: 'application/pdf' });

					const link = document.createElement('a');
					link.href = URL.createObjectURL(blob);
					link.download = `Dossier-${this.check.lastName || ''}-${this.check.firstName || ''}.pdf`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
					URL.revokeObjectURL(link.href);
				} else {
					throw new Error("Fichier non disponible.");
				}
			} catch (error) {
				alert(error?.response?.data?.message || this.$t('backgroundCheck.detail.exportError'));
			} finally {
				this.exporting = false;
			}
		},

		async generateNotes() {
			this.notesLoading = true;
			try {
				const id = this.$route.params.id;
				const response = await HTTP.post(`/background-check/${id}/generate-notes`);
				this.aiAnalysis = response.data.aiAnalysis;
				this.confidenceScore = response.data.score;
				this.autoApproved = response.data.autoApproved || false;
				if (this.autoApproved) this.check.status = "approved";
			} catch (error) {
				alert(error?.response?.data?.message || this.$t('backgroundCheck.detail.generateError'));
			} finally {
				this.notesLoading = false;
			}
		},

		async showSelfieModal() {
			this.selfieModalVisible = true;
			if (this.selfieUrl) return; // Already loaded
			this.selfieLoading = true;
			try {
				const id = this.$route.params.id;
				// The API proxies the image — we get it as a blob
				const response = await HTTP.get(`/background-check/${id}/veriff-media`, { params: { context: "face" }, responseType: "blob" });
				this.selfieUrl = URL.createObjectURL(response.data);
			} catch (error) {
				alert(error?.response?.data?.message || this.$t('backgroundCheck.detail.selfieError'));
				this.selfieModalVisible = false;
			} finally {
				this.selfieLoading = false;
			}
		},

		async retryGapAnalysis() {
			this.gapLoading = true;
			try {
				const id = this.$route.params.id;
				await HTTP.post(`/background-check/${id}/retry-gap-analysis`);
				// Poll for result
				const poll = setInterval(async () => {
					const response = await HTTP(`/background-check/${id}`);
					if (response.data.gapAnalysis?.status !== "pending") {
						clearInterval(poll);
						this.check = response.data;
						this.gapLoading = false;
					}
				}, 3000);
			} catch (error) {
				alert(error?.response?.data?.message || this.$t('backgroundCheck.detail.retryAnalysisError'));
				this.gapLoading = false;
			}
		},

		async retryB3Analysis() {
			this.b3Loading = true;
			try {
				const id = this.$route.params.id;
				await HTTP.post(`/background-check/${id}/retry-b3-analysis`);
				// Poll for result
				const poll = setInterval(async () => {
					const response = await HTTP(`/background-check/${id}`);
					if (response.data.b3Analysis?.status !== "pending") {
						clearInterval(poll);
						this.check = response.data;
						this.b3Loading = false;
					}
				}, 3000);
			} catch (error) {
				alert(error?.response?.data?.message || this.$t('backgroundCheck.detail.retryB3AnalysisError'));
				this.b3Loading = false;
			}
		},

		async resendVerificationLink() {
			this.resendLoading = true;
			try {
				const id = this.$route.params.id;
				const response = await HTTP.post(`/background-check/${id}/resend-verification`);
				// L'URL/l'id Veriff ont pu être recréés côté serveur
				if (response.data?.verification)
					this.check.verification = response.data.verification;
				this.resendSent = true;
				setTimeout(() => { this.resendSent = false; }, 3000);
			} catch (error) {
				alert(error?.response?.data?.message || this.$t('backgroundCheck.detail.resendVerificationLinkError'));
			} finally {
				this.resendLoading = false;
			}
		},

		async updateStatus(status) {
			this.statusLoading = true;
			try {
				const id = this.$route.params.id;
				const response = await HTTP.put(`/background-check/${id}/status`, {
					status,
					manualNotes: this.manualNotes || undefined,
				});
				this.check = response.data;
				this.manualNotes = this.check.manualNotes || "";
				alert(this.$t('backgroundCheck.detail.statusUpdated'));
			} catch (error) {
				alert(error?.response?.data?.message || this.$t('backgroundCheck.detail.statusUpdateError'));
			} finally {
				this.statusLoading = false;
			}
		},

		formatDate(date) {
			if (!date) return "—";
			return moment(date).format("DD/MM/YYYY");
		},

		formatDateTime(date) {
			if (!date) return "—";
			return moment(date).format("DD/MM/YYYY HH:mm");
		},

		statusBadgeClass(status) {
			const map = {
				"submitted": "bg-label-info",
				"in-review": "bg-label-warning",
				"approved": "bg-label-success",
				"rejected": "bg-label-danger",
			};
			return map[status] || "bg-label-secondary";
		},

		statusLabel(status) {
			const map = {
				"submitted": this.$t('backgroundCheck.status.submitted'),
				"in-review": this.$t('backgroundCheck.status.inReview'),
				"approved": this.$t('backgroundCheck.status.approved'),
				"rejected": this.$t('backgroundCheck.status.rejected'),
			};
			return map[status] || status;
		},

		veriffBadgeClass(verification) {
			if (!verification || !verification.status) return "bg-label-secondary";
			const map = {
				"init": "bg-label-secondary",
				"started": "bg-label-info",
				"submitted": "bg-label-warning",
				"approved": "bg-label-success",
				"declined": "bg-label-danger",
				"resubmission_requested": "bg-label-warning",
				"expired": "bg-label-danger",
				"abandoned": "bg-label-danger",
			};
			return map[verification.status] || "bg-label-secondary";
		},

		veriffLabel(verification) {
			if (!verification || !verification.status) return "—";
			const map = {
				"init": this.$t('backgroundCheck.verification.pending'),
				"started": this.$t('backgroundCheck.verification.inProgress'),
				"submitted": this.$t('backgroundCheck.verification.submitted'),
				"approved": this.$t('backgroundCheck.verification.verified'),
				"declined": this.$t('backgroundCheck.verification.refused'),
				"resubmission_requested": this.$t('backgroundCheck.verification.resubmission'),
				"expired": this.$t('backgroundCheck.verification.expired'),
				"abandoned": this.$t('backgroundCheck.verification.abandoned'),
			};
			return map[verification.status] || verification.status;
		},

		renderMarkdown(text) {
			return marked(text || "");
		},

		b3BorderClass() {
			if (!this.check?.b3Analysis?.status) return "";
			const map = { "clean": "border-success", "mismatch": "border-warning", "not_clean": "border-danger", "unreadable": "" };
			return map[this.check.b3Analysis.status] || "";
		},

		documentTypeLabel(type) {
			const map = {
				"drivers_license": this.$t('backgroundCheck.detail.driversLicense'),
				"passport": this.$t('backgroundCheck.detail.passport'),
				"id_card": this.$t('backgroundCheck.detail.idCard'),
				"residence_permit": this.$t('backgroundCheck.detail.residencePermit'),
			};
			return map[type] || type;
		},
	},

	async mounted() {
		await this.fetchDetail();
	},
};
</script>
